/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { Badge, ClickAwayListener, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useShoppingCartItemsState } from "../hooks/useShoppingCartItemsState";
import { useSessionState } from "../hooks/useSessionState";
import { useThemeState } from "../hooks/useThemeState";
import { useCatalogState } from "../hooks/useCatalogState";
import { AnimatePresence, motion } from "framer-motion";
import { useLocaleState } from "../hooks/useLocaleState";
import { useHookstate, useHookstateCallback, useHookstateEffect } from "@hookstate/core";
import { useLoginFormState } from "../hooks/useLoginFormState";
import { useRegisterFormState } from "../hooks/useRegisterFormState";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { useSiteLayoutState } from "../hooks/useSiteLayoutState";
import { useAppConfigState } from "../hooks/useAppConfigState";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { useInventoryState } from "../hooks/useInventoryState";
import React from "react";
import { useCheckoutFormState } from "../hooks/useCheckoutFormState";
import dayjs from "dayjs";
import { useStorefrontSettingsState } from "../hooks/useStorefrontSettingsState";
import { AppConstants } from "../data/AppConstants";
import { useTailwindMediaQueries } from "../hooks/useTailwindMediaQueries";
import { CheckIcon, LogInIcon, LogOutIcon, MoonIcon, ShoppingCartIcon, SquareAsteriskIcon, SunIcon, UserIcon, UserPlusIcon, UserXIcon, XIcon } from "lucide-react";

let lucideIconButtonSize: number = 28;
let lucideIconMenuSize: number = 22;
type SiteLayoutProps = {
    title?: string
    companyName?: string
    fullLeftGutter?: React.ReactNode
    mainLeftGutter?: React.ReactNode
    mainHeader?: React.ReactNode
    mainChildren?: React.ReactNode
    mainFooter?: React.ReactNode
    mainRightGutter?: React.ReactNode
    rightGutter?: React.ReactNode
    afterLogo?: React.ReactNode
    topRightMenu?: React.ReactNode
}
export const SiteLayout: React.FC<SiteLayoutProps> = props => {
    const themeState = useThemeState();
    //const themeName = themeState.get().themeName;
    const sessionState = useSessionState();
    const siteLayoutState = useSiteLayoutState();
    const loginFormState = useLoginFormState();
    const contactRegistrationFormState = useRegisterFormState();
    useDocumentTitle(props.title ?? '');
    const shoppingCartItemsState = useShoppingCartItemsState();
    const appConfigState = useAppConfigState();
    const appConfig = appConfigState.get().appConfig;
    //let shoppingCartCount = shoppingCartItemsState.get().length;
    let shoppingCartCount = 0;
    let items = shoppingCartItemsState.get();
    for (let i = 0; i < items.length; i++) {
        shoppingCartCount += items[i].qty;
    }
    const catalogState = useCatalogState();
    //const isDesktop = useIsDesktopMediaQuery();
    const mediaQueries = useTailwindMediaQueries();
    const inventoryState = useInventoryState();
    const checkoutFormState = useCheckoutFormState();
    const fromDateValue = checkoutFormState.get().fromDateValue;
    const toDateValue = checkoutFormState.get().toDateValue;
    const today = dayjs().startOf('day');

    const storefrontSettingsState = useStorefrontSettingsState();
    const isAnonymousAccessEnabled = storefrontSettingsState.get().settings?.IsAnonymousAccessEnabled ?? false;
    const isUniversity = storefrontSettingsState.get().settings?.IsUniversity ?? false;
    const filterCatalogsByLocation = storefrontSettingsState.get().settings?.FilterCatalogsByLocation ?? false;
    const isInventoryPage = window.location.pathname.startsWith('/storefront/inventory');
    const isShoppingCartPage = window.location.pathname.startsWith('/storefront/shoppingcart');
    const isCheckoutPage = window.location.pathname.startsWith('/storefront/checkout');
    const isProductPage = window.location.pathname.startsWith('/storefront/product');
    const logggedIn = sessionState.get().loggedIn;
    const hasLoginPopupCloseButton = storefrontSettingsState.get().settings?.IsAnonymousAccessEnabled ?? false;
    const shoppingCartEnabled = ((isAnonymousAccessEnabled || logggedIn) && (shoppingCartCount > 0));
    let iconButtonSize: 'small' | 'medium' | 'large' = "large";
    // if (mediaQueries.desktop) {
    //     iconButtonSize = "medium";
    // }

    // create a callback function to create memoized function that won't change every render and trigger useEffect
    const onInit = useHookstateCallback(() => {
        loginFormState.setIsPopup(true);
    }, [loginFormState])

    useHookstateEffect(() => {
        loginFormState.setIsPopup(true);

        // update dates on checkoutFormState
        let fromDate = fromDateValue;
        let toDate = toDateValue;
        
        if (dayjs(fromDate).isBefore(today)) {
            fromDate = today.format('YYYY-MM-DD');
            toDate = dayjs(fromDate).add(6, 'day').format('YYYY-MM-DD');
            checkoutFormState.setDates(fromDate, toDate);
        }
        if (dayjs(toDate).isBefore(fromDate)) {
            toDate = dayjs(fromDate).add(6, 'day').format('YYYY-MM-DD');
            checkoutFormState.setToDate(toDate);
        }
    }, [onInit, today, fromDateValue, toDateValue]);

    useHookstateEffect(() => {
        if (sessionState.get().loggedIn) {
            //console.log('refresh catalogs');
            catalogState.loadAsync(true);
        }
    }, [sessionState.get().loggedIn])

    let catalogs = catalogState.get().catalogs || [];
    const hasLocationSelector = isInventoryPage || isShoppingCartPage || isCheckoutPage || isProductPage;
    if (filterCatalogsByLocation && !isUniversity && (hasLocationSelector)) {
        //catalogs = catalogs.filter(c => c.WarehouseId === checkoutFormState.getWarehouse()?.WarehouseId);
        catalogs = catalogState.getWarehouseCatalogs(checkoutFormState.getWarehouse()?.WarehouseId);
    }
    //const inventory = inventoryState.get();
    //console.log({ catalogs, inventory })

    useHookstateEffect(() => {
        //console.log(checkoutFormState.get().warehouseIdValue)
        inventoryState.updateSelectedCatalog();
    }, [checkoutFormState.get().warehouseIdValue]);

    const catalogLinks = catalogs.map((catalog) => {
        const isSelected = (inventoryState.get().catalog) && (catalog.Id === (inventoryState.get().catalog?.Id ?? ''));

        return (
            <li key={catalog.Id}>
                <Link to={`/inventory/${catalog.Id}/index`} css={css`text-decoration:none;`}>
                    <div
                        css={css`
                            flex:1 1 0;
                            font-size:.8rem;
                            white-space:no-wrap;
                            padding:0 2em 0 0;
                            color: ${(isSelected) ? themeState.get().theme.palette.siteAccent : themeState.get().theme.palette.siteTextPrimary};
                            transition: color .1s;
                            &:hover {
                                color: ${themeState.get().theme.palette.siteAccent}
                            }
                            `}>
                        {catalog.Name}
                    </div>
                </Link>
            </li>
        );
    });

    return <>
        <div css={css`
                position:fixed;
                top:0;
                right:0;
                bottom:0;
                left:0;
                display:flex;
                overflow:hidden;
                background-color: ${themeState.get().theme.palette.siteBackground};
                color:${themeState.get().theme.palette.siteTextPrimary};
                transition: all 1s;`}>
            <motion.div css={css`flex:0 0 auto;display:flex;flex-direction:column;overflow:hidden;`} layout>
                {props.fullLeftGutter}
            </motion.div>
            <motion.div css={css`flex:1 1 0;display:flex;flex-direction:column;` } layout>
                <header
                    css={css`
                        flex: 0 1 auto;
                        display: flex;
                        flex-direction: column;
                        justify-content: start;
                        background-color: ${themeState.get().theme.palette.siteHeaderBackground};
                        transition: background-color 1s;`}>
                    <div
                        css={css`
                            flex:0 0 auto;
                            display:flex;
                            align-items: center;`}>
                        <div
                            css={css`
                                flex: 1 1 0;
                                display:flex;
                                margin:0 auto;
                                max-width:${AppConstants.siteWidthCss};` }>

                            {/* Company Logo */}
                            {appConfig && (
                                <div
                                    css={css`
                                        flex: 0 0 auto;
                                        display: flex;
                                        align-items:center;`}>
                                    <div css={css`
                                        margin:4px 8px;
                                        display:flex;
                                        align-items:center;
                                        img {
                                            max-height:64px;
                                            max-width:128px;
                                        }
                                        `}>
                                        <Link to="/">
                                                <img
                                                    src={appConfig.CompanyLogoUrl}
                                                    alt={appConfig.CompanyName}
                                                    onClick={async e => {
                                                        return Promise.resolve().then(() => {
                                                            inventoryState.clearCatalogFilterAsync();
                                                        });
                                                    }}
                                                />
                                        </Link> 
                                        {props.afterLogo}
                                    </div>
                                    {/*<div css={css`*/}
                                    {/*    font-family: 'Ropa Sans';*/}
                                    {/*    font-size:2em;*/}
                                    {/*    white-space:nowrap;*/}
                                    {/*    @media screen and (width < 600px) {*/}
                                    {/*    & {*/}
                                    {/*        display: none;*/}
                                    {/*    }*/}
                                    {/*}`}>{siteConfigState.get()?.siteConfig?.CompanyName ?? ""}</div>*/}
                                </div>
                            )}

                            <div css={css`
                                flex: 1 1 0;
                                @media print {
                                    & {
                                        display: none;
                                    }
                            `}>

                                <div css={css`
                                    flex: 1 1 0;
                                    display: flex;
                                    align-items: center;
                                    justify-content: flex-end;
                                    margin: 16px 0 0 0;`}>
                                    {mediaQueries.minw_md && (<ul
                                        css={css`
                                            flex:1 1 0;
                                            padding: 0;
                                            display: flex;
                                            flex-wrap: wrap;
                                            align-items: center;
                                            justify-content: flex-end;
                                            /*li:first-of-type {
                                                list-style-type: none;
                                            }*/`}>
                                        {catalogLinks}
                                    </ul>)}

                                    {/*{isMobile && (<>*/}
                                    {/*    <div*/}
                                    {/*        css={css`*/}
                                    {/*            flex:1 1 0;*/}
                                    {/*            display: none;*/}
                                    {/*            @media (min-width: 720px) {*/}
                                    {/*                display:flex;*/}
                                    {/*            }`}>*/}
                                    {/*        <CatalogMenu />*/}
                                    {/*    </div>*/}
                                    {/*</>)}*/}

                                    <div css={css`flex: 0 0 auto;display:flex;align-items:center;`}>
                                        {props.topRightMenu}

                                        {/* <Tooltip title={siteLayoutState.translate('Theme') + ': ' + ((themeState.get().themeName === 'dark') ? siteLayoutState.translate('Dark') : siteLayoutState.translate('Light'))} >
                                            <IconButton size={iconButtonSize} aria-label="toggle light and dark mode"
                                                onClick={async e => {
                                                    return Promise.resolve().then(() => {
                                                        let newThemeName = (themeState.get().themeName === 'dark') ? 'light' : 'dark'
                                                        themeState.set(newThemeName);;
                                                    });
                                                }} >
                                                {themeState.get().themeName === 'dark' &&
                                                    <DarkMode fontSize={iconButtonSize} />
                                                }
                                                {themeState.get().themeName === 'light' &&
                                                    <LightMode fontSize={iconButtonSize} />
                                                }
                                            </IconButton>
                                        </Tooltip> */}

                                        {/* <TranslateMenu iconButtonSize={iconButtonSize} /> */}
                                        {shoppingCartEnabled && (<>
                                            <Tooltip title={siteLayoutState.translate('Shopping Cart')} >
                                                <Link to="/shoppingcart">
                                                    <Badge badgeContent={shoppingCartCount} max={99999} css={css`.MuiBadge-badge {background-color: #4caf50; !important;color: #ffffff !important}`}>
                                                        <IconButton size={iconButtonSize} aria-label="shopping cart">
                                                            <ShoppingCartIcon size={lucideIconButtonSize} />
                                                            {/*<ShoppingCart fontSize={iconButtonSize} />*/}
                                                        </IconButton>
                                                    </Badge>
                                                </Link>
                                            </Tooltip>

                                        </>)}
                                        {!shoppingCartEnabled && (<>
                                            <Tooltip title={siteLayoutState.translate('Shopping Cart')} >
                                                <span>
                                                    <IconButton size={iconButtonSize} aria-label="shopping cart" disabled>
                                                        <ShoppingCartIcon size={lucideIconButtonSize} />
                                                        {/*<ShoppingCart fontSize={iconButtonSize} />*/}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                        </>)}
                                        {(isAnonymousAccessEnabled || logggedIn) && (<>
                                            <AccountMenu iconButtonSize={iconButtonSize} />
                                        </>)}
                                    </div>
                                </div>
                                <div css={css`
                                    flex: 1 1 0;
                                    display: flex;
                                    align-items: center;
                                    justify-content: flex-end;
                                    padding: 0 1em;
                                    font-size:.8em;`}>
                                    <div>{sessionState.get().email}</div>
                                </div>

                            </div>
                        </div>

                    </div>

                    {/*<div*/}
                    {/*    css={css`*/}
                    {/*            flex:0 0 auto;*/}
                    {/*            display: flex;*/}
                    {/*            flex-wrap:wrap;*/}
                    {/*            justify-content: center;*/}
                    {/*            @media (min-width: 720px) {*/}
                    {/*                display:none;*/}
                    {/*                justify-content: flex-end;*/}
                    {/*            }*/}
                    {/*        `}>*/}
                    {/*    <CatalogMenu />*/}
                    {/*</div>*/}
                
                </header>

                <main
                    css={css`
                        flex: 1 1 0;
                        display: flex;
                        overflow: hidden;
                        max-width:100vw;`}>
                    <div
                        css={css`
                            flex: 0 0 auto;
                            display: flex;
                            flex-direction: column;
                            overflow: auto;`}>
                        {props.mainLeftGutter}
                    </div>
                    <div css={css`flex:1 1 0;display:flex;flex-direction:column;overflow:auto;`}>
                        <div css={css`flex:0 0 auto;display:flex;justify-content:flex-start;`}>
                            {props.mainHeader}
                        </div>
                        <div css={css`
                            flex:1 1 0;
                            display:flex;
                            flex-direction:column;
                            justify-content:flex-start;
                            @media screen {
                                overflow:hidden;
                            }
                            `}>
                            {props.mainChildren}
                        </div>
                        <div css={css`flex:0 0 auto;display:flex;flex-direction:column;` }>
                            {props.mainFooter}
                        </div>
                        <div css={css`flex:0 0 auto;display:flex;` }>
                            <div css={css`display:flex;font-weight:bold` }>
                                {/* <FooterLink name="Privacy" href="/privacy" />
                                <FooterLink name="About" href="/about" /> */}
                            </div>
                        </div>
                    </div>
                    <div css={css`flex:0 0 auto;display:flex;flex-direction:column;overflow:auto;`}>{props.mainRightGutter}</div>
                </main>
                {/*background-color: ${themeState.get().theme.palette.siteFooterBackground};*/}
                <footer
                    css={css`
                        display:flex:0 0 auto;
                        display:flex;
                        justify-content:center;
                        background-color: #333333;
                        padding-left: 44px;
                        @media (min-width: 768px) {
                            padding-left: 0;
                        }`}>
                    {/*<div css={css`*/}
                    {/*    font-size:.6rem;*/}
                    {/*    color:#777777;*/}
                    {/*    a {*/}
                    {/*        color:#777777;*/}
                    {/*        text-decoration: none;*/}
                    {/*    }*/}
                    {/*    a:hover {*/}
                    {/*        text-decoration: underline;*/}
                    {/*    }*/}
                    {/*    `}>*/}
                    {/*    <a href="https://dbworks.com/products/rentalworks/">RentalWorks&reg; Storefront</a> v{version} &copy;{year}  <a href="https://dbworks.com/">Database Works, Inc.</a> All rights reserved.*/}
                    {/*</div>*/}
                </footer>
            </motion.div>
            <motion.div css={css`flex:0 0 auto;display:flex;flex-direction:column;`} layout>{props.rightGutter}</motion.div>

        </div>
        {/*<ReactTooltip
            id={siteLayoutState.get().tooltipId ?? 'numberbox'}
            isOpen={showTooltip}
            content={siteLayoutState.get().tooltipMessage}
            variant={siteLayoutState.get().tooltipVariant}
            place={siteLayoutState.get().tooltipPlace}
            positionStrategy="fixed"
        />*/}
        <AnimatePresence>
            {siteLayoutState.get().loginFormPopupVisible && (
                <div
                    css={css`
                    background-color:rgba(0,0,0,.5);
                    position:fixed;
                    top:0;
                    left:0;
                    right:0;
                    bottom:0;
                    z-index:1;
                    display:flex;
                    align-items:center;
                    justify-content:center;`}
                    onClick={async e => {
                        //return Promise.resolve().then(() => {
                        //    siteLayoutState.setLoginFormPopupVisible(false)
                        //});
                    }}>
                    <motion.div
                        css={css`
                        margin: 1em;
                        padding: 1em;
                        z-index: 2;
                        background-color: ${themeState.get().theme.palette.siteBackground};
                        overflow:hidden;
                        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
                        position: relative;`}
                        layout
                        layoutId="loginform"
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        onClick={async e => { e.stopPropagation(); }}>
                        {hasLoginPopupCloseButton && (
                            <IconButton size={iconButtonSize} css={css`position:absolute;top:8px;right:8px;`} onClick={e => {
                                return Promise.resolve().then(() => {
                                    siteLayoutState.setLoginFormPopupVisible(false)
                                });
                            }} ><XIcon size={lucideIconButtonSize} /></IconButton>
                        )}
                        <LoginForm />
                    </motion.div>
                </div>
            )}

            {siteLayoutState.get().resetPasswordFormPopupVisible && (
                <div
                    css={css`
                    background-color:rgba(0,0,0,.5);
                    position:fixed;
                    top:0;
                    left:0;
                    right:0;
                    bottom:0;
                    z-index:1;
                    display:flex;
                    align-items:center;
                    justify-content:center;`}
                    onClick={async e => {
                        //return Promise.resolve().then(() => {
                        //    siteLayoutState.setResetPasswordFormPopupVisible(false)
                        //});
                    }}>
                    <motion.div
                        css={css`
                        margin: 1em;
                        padding: 1em;
                        z-index: 2;
                        background-color: ${themeState.get().theme.palette.siteBackground};
                        overflow:hidden;
                        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
                        position: relative;`}
                        layout
                        layoutId="loginform"
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        onClick={async e => { e.stopPropagation(); }}>
                        <IconButton size={iconButtonSize} css={css`position:absolute;top:8px;right:8px;`} onClick={e => {
                            return Promise.resolve().then(() => {
                                siteLayoutState.setResetPasswordFormPopupVisible(false)
                            });
                        }} ><XIcon size={lucideIconButtonSize} /></IconButton>
                        <ResetPasswordForm type="resetpassword" />
                    </motion.div>
                </div>
            )}

            {siteLayoutState.get().registerFormPopupVisible && (
                <div
                    css={css`
                    background-color:rgba(0,0,0,.5);
                    position:fixed;
                    top:0;
                    left:0;
                    right:0;
                    bottom:0;
                    z-index:1;
                    display:flex;
                    align-items:center;
                    justify-content:center;`}
                    onClick={async e => {
                        //return Promise.resolve().then(() => {
                        //    siteLayoutState.setRegisterFormPopupVisible(false)
                        //});
                    }}>
                    <motion.div
                        css={css`
                        margin: 1em;
                        padding: 1em;
                        z-index: 2;
                        background-color: ${themeState.get().theme.palette.siteBackground};
                        overflow:hidden;
                        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
                        position: relative;
                        flex: 1 1 0;
                        max-width: 400px;
                        `}
                        layout
                        layoutId="registerform"
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        onClick={async e => { e.stopPropagation(); }}>
                        <IconButton size={iconButtonSize} css={css`position:absolute;top:8px;right:8px;`} onClick={e => {
                            return Promise.resolve().then(() => {
                                siteLayoutState.setRegisterFormPopupVisible(false)
                            });
                        }} ><XIcon size={lucideIconButtonSize} /></IconButton>
                        <RegisterForm registerFormState={contactRegistrationFormState}></RegisterForm>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    </>
}



type AccountMenuProps = {
    iconButtonSize?: "small" | "medium" | "large";
}
const AccountMenu: React.FC<AccountMenuProps> = props => {
    const sessionState = useSessionState();
    const siteLayoutState = useSiteLayoutState();

    // menu
    const anchorElState = useHookstate<null | HTMLElement>(null);
    const openAccountMenu = Boolean(anchorElState.get());
    const showAccountMenu = async (event: React.MouseEvent<HTMLButtonElement>) => {
        anchorElState.set(event.currentTarget);
    };
    const closeAccountMenu = (language?: string) => {
        anchorElState.set(null);
        if (language) {
            localeState.setLanguage(language);
        }
    };

    let resetPasswordType: 'change' | 'reset' = (sessionState.get().loggedIn) ? 'change' : 'reset';
    let resetPasswordText = (sessionState.get().loggedIn) ? siteLayoutState.translate('Change Password') : siteLayoutState.translate('Reset Password');
    const version = process.env.REACT_APP_VERSION ?? '0.0.0';
    const year = dayjs().year();
    const themeState = useThemeState();

    const localeState = useLocaleState();
    const currentLanguage = localeState.get().language;
    const storefrontSettingsState = useStorefrontSettingsState();
    const registerEnabled = storefrontSettingsState.get().settings?.RegisterEnabled ?? false;

    return (<>
        <ClickAwayListener onClickAway={async e => {
            return Promise.resolve().then(() => {
                closeAccountMenu();
            });
        }}>
            <div>
                <Tooltip title={siteLayoutState.translate('User Account Menu')}>
                    <IconButton  size={props.iconButtonSize ?? "large"}aria-label="account"
                        id="account-button"
                        aria-controls={openAccountMenu ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={openAccountMenu ? 'true' : undefined}
                        css={css`/*color:${sessionState.get().loggedIn ? '#ffffff' : '#ff0000'};*/`}
                        onClick={showAccountMenu}>
                        {sessionState.get().loggedIn && (
                            <UserIcon size={lucideIconButtonSize} />
                        )}
                        {!sessionState.get().loggedIn && (
                            <UserXIcon size={lucideIconButtonSize} />
                        )}
                    </IconButton>
                </Tooltip>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorElState.get() as HTMLButtonElement}
                    open={openAccountMenu}
                    onClose={e => { closeAccountMenu(); }}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    <MenuItem
                        disabled={sessionState.get().loggedIn}
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                closeAccountMenu();
                                siteLayoutState.setLoginFormPopupVisible(true);
                            });
                        }}>
                        <ListItemIcon>
                            <LogInIcon size={lucideIconMenuSize} />
                        </ListItemIcon>
                        <ListItemText>{siteLayoutState.translate('Login')}</ListItemText>                        
                    </MenuItem>
                    {registerEnabled && (
                        <MenuItem
                            disabled={sessionState.get().loggedIn}
                            onClick={async e => {
                                return Promise.resolve().then(() => {
                                    closeAccountMenu();
                                    siteLayoutState.setRegisterFormPopupVisible(true);
                                });
                            }}>
                            <ListItemIcon>
                                <UserPlusIcon size={lucideIconMenuSize} />
                            </ListItemIcon>
                            <ListItemText>{siteLayoutState.translate('Register')}</ListItemText>  
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                closeAccountMenu();
                                if (sessionState.get().loggedIn) {
                                    siteLayoutState.sendResetPasswordEmailAsync(resetPasswordType);
                                } else {
                                    siteLayoutState.setResetPasswordFormPopupVisible(true);
                                }
                            });
                        }}>
                        <ListItemIcon>
                            <SquareAsteriskIcon size={lucideIconMenuSize} />
                        </ListItemIcon>
                        <ListItemText>{resetPasswordText}</ListItemText>  
                    </MenuItem>

                    {/*<MenuItem*/}
                    {/*    disabled>Quotes</MenuItem>*/}
                    {/*<MenuItem*/}
                    {/*    disabled*/}
                    {/*    onClick={e => {*/}
                    {/*        closeAccountMenu();*/}
                    {/*        navigate('/settings');*/}
                    {/*    }}>Settings</MenuItem>*/}
                    <Divider />
                    <MenuItem
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                closeAccountMenu();
                                if (themeState.isDark()) {
                                    themeState.set('light');
                                } else {
                                    themeState.set('dark');
                                }
                            });
                        }}>
                        <ListItemIcon>
                            {!themeState.isDark() && (
                                <MoonIcon size={lucideIconMenuSize} />
                            )}
                            {themeState.isDark() && (
                                <SunIcon size={lucideIconMenuSize} />
                            )}
                        </ListItemIcon>
                        <ListItemText>
                            {!themeState.isDark() && (
                                <span>{siteLayoutState.translate('Dark Theme')}</span>
                            )}
                            {themeState.isDark() && (
                                <span>{siteLayoutState.translate('Light Theme')}</span>
                            )}
                        </ListItemText>  
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                closeAccountMenu('en');
                            });
                        }}
                    >
                         <ListItemIcon>
                            {currentLanguage === 'en' && (
                                <CheckIcon size={lucideIconMenuSize} />
                            )}
                        </ListItemIcon>
                        English
                    </MenuItem>
                    <MenuItem
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                closeAccountMenu('es');
                            });
                        }}
                    >
                        <ListItemIcon>
                            {currentLanguage === 'es' && (
                                <CheckIcon size={lucideIconMenuSize} />
                            )}
                        </ListItemIcon>
                        Español
                    </MenuItem>
                    <MenuItem onClick={async e => {
                        return Promise.resolve().then(() => {
                            closeAccountMenu('fr');
                        });
                    }}>
                        <ListItemIcon>
                            {currentLanguage === 'fr' && (
                                <CheckIcon size={lucideIconMenuSize} />
                            )}
                        </ListItemIcon>
                        Français
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        disabled={!sessionState.get().loggedIn}
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                closeAccountMenu();
                                siteLayoutState.logoutAsync();
                            });
                        }}>
                        <ListItemIcon>
                            <LogOutIcon size={lucideIconMenuSize} />
                        </ListItemIcon>
                        <ListItemText>{siteLayoutState.translate('Logout')}</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem disabled>
                        <div css={css`font-size:.7rem;text-align:center;`}>
                            <div>RentalWorks&reg; v{version}</div>
                            <div>&copy;{year} Database Works, Inc.</div>
                        </div>
                    </MenuItem>
                </Menu>
            </div>
        </ClickAwayListener>
    </>);
}
